export interface DeckPage {
  id: string
  label: string
}

/** The REKOVELLE story, top to bottom — mirrors the Filtek build-up. */
export const deckPages: DeckPage[] = [
  { id: 'opening', label: 'All you need is love' },
  { id: 'congress', label: 'ESHRE 2027 · The IVF Pulse' },
  { id: 'challenge', label: 'The attention problem' },
  { id: 'journey', label: 'Attract. Convince. Convert.' },
  { id: 'website', label: 'Snakify the website' },
  { id: 'channels', label: 'One experience. Many entry points.' },
  { id: 'useCases', label: 'One format. Many use cases.' },
  { id: 'signals', label: 'Every interaction creates a signal' },
  { id: 'compound', label: 'Make that data compound' },
  { id: 'station', label: 'Snakker Station' },
  { id: 'campaign', label: 'The brand-love campaign' },
]

/* ── 01 · Opening (follow-up framing) ───────────────────────── */

export const snakTraits = [
  { title: 'Short', body: '30–60 seconds of HCP attention.' },
  { title: 'Active', body: 'HCPs choose, predict and respond.' },
  { title: 'Measurable', body: 'Every interaction creates a signal.' },
]

/* ── 02 · ESHRE 2027 · The IVF Pulse ────────────────────────── */

/** Live prototype of the connected congress campaign. */
export const ivfPulseUrl = 'https://project-ivf-pulse-survey-experience-717.magicpatterns.app'

export const ivfPulseMoments = [
  { number: '01', moment: 'Before & during ESHRE', name: 'IVF Pulse', verbs: 'Attract & learn', accent: '#0F9C8C' },
  { number: '02', moment: 'At the Ferring booth', name: 'REKOVELLE Booth', verbs: 'Engage & convince', accent: '#5468D8' },
  { number: '03', moment: 'After ESHRE', name: 'IVF Pulse Results', verbs: 'Reconnect & learn', accent: '#7A5AC8' },
]

/* ── 03 · The attention problem ─────────────────────────────── */

export const snakWalkthrough = [
  { screen: 0, alt: 'Hook: two patients — would you approach them the same way?', caption: 'Two patients. Same approach?' },
  { screen: 1, alt: 'Reframe: the goal isn’t maximum stimulation', caption: 'The goal isn’t maximum stimulation.' },
  { screen: 2, alt: 'Mini-simulator: change AMH and weight, see the challenge', caption: 'Change the patient. See the challenge.' },
  { screen: 3, alt: 'Reveal: REKOVELLE starts with the patient', caption: 'REKOVELLE starts with the patient.' },
  { screen: 5, alt: 'Proof: individualization with evidence behind it', caption: 'Individualization with evidence behind it.' },
]

export const snakPrinciples = [
  'Short enough to finish.',
  'Interesting enough to continue.',
  'Interactive enough to learn from.',
]

export const snakArc = ['Story', 'Choice', 'Insight', 'Action']

/* ── 03 · Attract. Convince. Convert. ───────────────────────── */

export type StageId = 'attract' | 'convince' | 'convert'

export const journey: { id: StageId; name: string; channels: string; goal: string }[] = [
  {
    id: 'attract',
    name: 'Attract',
    channels: 'Hooks and teasers via email, congress, IVA and face-to-face.',
    goal: 'Bring fertility specialists into a 40-second experience.',
  },
  {
    id: 'convince',
    name: 'Convince',
    channels: 'REKOVELLE, Snakified — two patients, one clinical question, one reveal.',
    goal: 'Let HCPs experience why the starting dose should start with the patient.',
  },
  {
    id: 'convert',
    name: 'Convert',
    channels: 'Approved evidence, dosing guidance, pen demo and rep follow-up.',
    goal: 'Contextual next steps connect HCPs directly to Ferring’s approved content.',
  },
]

export const attractChannels = ['Email', 'Congress', 'IVA', 'F2F']

export type CtaId = 'evidence' | 'dosing' | 'pen' | 'rep'

export const convertCtas: { id: CtaId; short: string }[] = [
  { id: 'evidence', short: 'Evidence' },
  { id: 'dosing', short: 'Dosing guide' },
  { id: 'pen', short: 'Pen demo' },
  { id: 'rep', short: 'Talk to a rep' },
]

/* ── 04 · Snakify the website ───────────────────────────────── */

export const websiteSteps = [
  {
    key: 'A',
    title: 'The invitation',
    caption: 'A compact teaser on the existing REKOVELLE page invites HCPs to play a 40-second case.',
  },
  {
    key: 'B',
    title: 'The Snak opens',
    caption: 'One click opens the live Snak above the website. Close it anytime, or follow through to the approved evidence.',
  },
]

/* ── 05 · Channels ──────────────────────────────────────────── */

export type ChannelId =
  | 'email'
  | 'website'
  | 'symposium'
  | 'training'
  | 'qr'
  | 'congress'
  | 'iva'
  | 'booth'

export const shareChannels: { id: ChannelId; name: string; detail: string }[] = [
  { id: 'email', name: 'Email / CRM', detail: 'Newsletters and rep follow-ups' },
  { id: 'website', name: 'Brand website', detail: 'An optional lean-back layer' },
  { id: 'symposium', name: 'Symposium', detail: 'Live polls in the room' },
  { id: 'training', name: 'Training', detail: 'IVF nurses and new staff' },
  { id: 'qr', name: 'QR code', detail: 'Print, posters and leave-behinds' },
  { id: 'congress', name: 'Congress', detail: 'Pre-, during- and post-ESHRE' },
  { id: 'iva', name: 'Rep iPad / IVA', detail: 'Online and face-to-face detailing' },
  { id: 'booth', name: 'Booth / F2F', detail: 'A conversation opener' },
]

export const snakkerSupport = ['Adaptation', 'Production', 'Campaign deployment', 'Measurement', 'Optimization']

/* ── 06 · Use cases ─────────────────────────────────────────── */

export type UseCaseId = 'webinar' | 'website' | 'campaign' | 'congress' | 'clinical' | 'product'

export const useCaseCandidates: {
  id: UseCaseId
  label: string
  detail: string
  media: { screen: number; alt: string }[]
}[] = [
  {
    id: 'webinar',
    label: 'Webinar',
    detail: 'Turn one fertility event into short evergreen chapters.',
    media: [{ screen: 5, alt: 'Teaser Snak proof screen leading to the approved evidence' }],
  },
  {
    id: 'website',
    label: 'Website',
    detail: 'Add interaction to REKOVELLE pages already attracting HCPs.',
    media: [
      { screen: 0, alt: 'Teaser Snak hook: two patients, same approach?' },
      { screen: 2, alt: 'Teaser Snak mini-simulator with AMH and weight sliders' },
    ],
  },
  {
    id: 'campaign',
    label: 'Campaign content',
    detail: 'Turn approved REKOVELLE material into a short interactive campaign.',
    media: [{ screen: 3, alt: 'Teaser Snak reveal: an individualized starting dose' }],
  },
  {
    id: 'congress',
    label: 'Congress',
    detail: 'Create pre-, during- and post-ESHRE engagement.',
    media: [{ screen: 0, alt: 'Teaser Snak hook with a Yes / No poll' }],
  },
  {
    id: 'clinical',
    label: 'Clinical / dosing content',
    detail: 'Make the AMH + body-weight algorithm easier to explore.',
    media: [{ screen: 2, alt: 'Teaser Snak mini-simulator showing the response challenge' }],
  },
  {
    id: 'product',
    label: 'Product experience',
    detail: 'Make the benefit tangible — calculated for the patient, set on the pen.',
    media: [{ screen: 4, alt: 'Teaser Snak pen screen: starting dose set on the REKOVELLE pen' }],
  },
]

/* ── 07 · Signals ───────────────────────────────────────────── */

export const signalLoop = ['Signals', 'Next campaign', 'Better activation']

/* ── 08 · Compound ──────────────────────────────────────────── */

export const compoundFeatures = [
  {
    key: 'see',
    title: 'See what works',
    detail: 'Across brands, countries and channels.',
    image: 'https://cdn.magicpatterns.com/uploads/kAPX1sS6y8zcjhhHD6CLzo/Screenshot_2026-09-21_at_13.14.09.png',
    alt: 'Portfolio overview comparing gap severity by market and product',
  },
  {
    key: 'learn',
    title: 'Learn faster',
    detail: 'Apply success from one market to another.',
    image: 'https://cdn.magicpatterns.com/uploads/3feRkRv9AX1mR6BMuNMFrM/Screenshot_2026-09-21_at_13.13.56.png',
    alt: 'Single product view showing performance across every assessed market',
  },
]

export const compoundBenefits = [
  { key: 'reuse', title: 'Reuse more', detail: 'Adapt instead of rebuild.' },
  { key: 'spot', title: 'Spot opportunities', detail: 'Identify where proven ideas can travel.' },
  { key: 'buy', title: 'Build buying power', detail: 'More volume creates lower unit cost.' },
]

/* ── 09 · Snakker Station ───────────────────────────────────── */

export const stationScreenshot =
  'https://cdn.magicpatterns.com/uploads/9PgVysqJh8jC3Dw6oZmj8M/Screenshot_2026-10-22_at_11.03.14.png'

/** Pixel size of the Station screenshot the shelf thumbnails are cropped from. */
export const stationSource = { width: 2520, height: 1479 }
export const shelfThumb = { width: 313, height: 321 }

export const stationFilters = [
  'All',
  'Market research',
  'Rep support',
  'Banners / hooks',
  'Study update',
  'Congress',
  'Follow-up',
] as const

export type StationFilter = (typeof stationFilters)[number]

export interface ShelfSnak {
  id: string
  title: string
  category: Exclude<StationFilter, 'All'>
  x: number
  y: number
}

export const shelfSnaks: ShelfSnak[] = [
  { id: 'itch', title: 'Just an itch? — evidence challenge', category: 'Congress', x: 1075, y: 284 },
  { id: 'study', title: '87% — four-year study update', category: 'Study update', x: 1417, y: 284 },
  { id: 'pulse', title: 'Eye Care Pulse — peer poll', category: 'Congress', x: 1759, y: 284 },
  { id: 'booth', title: 'The analysis — booth iPad', category: 'Congress', x: 2101, y: 284 },
  { id: 'oct', title: '5 OCT variables — follow-up', category: 'Follow-up', x: 1075, y: 744 },
  { id: 'guide', title: 'Patient application guide', category: 'Rep support', x: 1417, y: 744 },
  { id: 'banner', title: 'Have you seen it yet? — banner', category: 'Banners / hooks', x: 1759, y: 744 },
]

export const featuredSnak = {
  category: 'Clinical / dosing',
  title: 'Two patients. Same approach? — REKOVELLE teaser',
  usedIn: 'Proposed for the first REKOVELLE wave',
  solves: 'Make individualized first-cycle dosing easy to experience and remember',
  channels: ['Email', 'Congress', 'IVA', 'F2F'],
}

export const stationPillars = [
  { label: 'Control', text: 'Control Tower provides central visibility.' },
  { label: 'Empower', text: 'Station gives local markets access to existing Snaks and inspiration.' },
  { label: 'Optimize', text: 'Performance intelligence, pulse insights and recommendations help everyone improve.' },
]

/* ── 10 · The brand-love campaign ───────────────────────────── */

/** Example screens from a live Snakker-managed campaign. */
export const campaignHook = {
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/8c12d81b-81c9-4950-a9df-8b72bc22f05b.jpg',
  alt: 'Example: WhatsApp conversation showing a banner ad reading “Seven minutes. One choice.”',
}

export const campaignTeasers = [
  {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/f56b4488-53cb-4caa-ae80-a720cbf1c3eb.jpg',
    alt: 'Example teaser Snak opening screen inviting the HCP to begin the case',
  },
  {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/58369ee1-e4a5-4920-bde3-b70e71b20ceb.jpg',
    alt: 'Example teaser Snak asking the HCP what they would prescribe, with three answer options',
  },
  {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/a9602e53-2402-4752-ac16-e8b3f93684ea.jpg',
    alt: 'Example teaser Snak answer reveal comparing the HCP’s choice with peer responses',
  },
]

export const campaignInsights = {
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/e0f59a14-3932-4db7-bd6d-4e3215b287c2.jpg',
  alt: 'Campaign analytics dashboard showing engagement, answer distribution and campaign performance',
}

export const campaignStages = [
  { name: 'Hook', detail: 'A banner where HCPs already are — WhatsApp, email, congress.' },
  { name: 'Teaser Snak', detail: 'A case, a choice and an immediate peer-benchmarked answer.' },
  { name: 'Monitoring & optimization', detail: 'We run it, read it and refine it — every tap briefs the next wave.' },
]

export const reuseBlocks = ['Thinking', 'Arguments', 'Evidence', 'Formats']

export const ferringQuestions = [
  {
    question: 'Which REKOVELLE benefit first?',
    options: ['Individualized dosing', 'Response control', 'Fewer OHSS interventions', 'The pen'],
  },
  { question: 'Where should it live?', options: ['Email', 'Congress', 'Symposium', 'IVA', 'F2F'] },
  {
    question: 'Who should play it?',
    options: ['Fertility specialists', 'IVF nurses', 'KOLs', 'Congress delegates'],
  },
]
